/**
 * Builds the karaoke timeline for the story reader: one entry per rendered
 * word span, carrying the DOM id the reader already assigns to that span
 * (`STORY-TITLE<i>` for title words, `WORD-<p><i>` for body words) plus the
 * time window in which the teacher reads it and the sentence it belongs to.
 *
 * Timings come from `story.audioAlignment` — character-level forced alignment
 * of the recording against `title + "\n" + paragraphs` (see the backend's
 * audio-alignment.helper.ts). Word windows are derived here by walking that
 * text in lockstep with the exact word objects the reader renders, so the
 * highlight can never disagree with tap-to-lookup about what a "word" is.
 */

import { splitIntoParagraphs } from './storySentences'

export interface KaraokeSpan {
  id: string
  startMs: number
  endMs: number
  sentence: number
  /** false for punctuation-only spans: they extend the sentence highlight but
   *  never get the solid word highlight (pauses tend to sit on punctuation). */
  isWord: boolean
}

const TAG_REGEX = /<[^>]+>/g
const SENTENCE_END_REGEX = /[。？！?!…]/
const WORD_CHAR_REGEX = /[㐀-鿿豈-﫿A-Za-z0-9]/
const WHITESPACE_REGEX = /\s/

interface AlignmentCursor {
  text: string
  starts: number[]
  ends: number[]
  pos: number
}

/** Match one rendered word at the cursor, tolerating whitespace on either
 *  side. Returns the [startMs, endMs] window or null on mismatch. */
function matchWord(cursor: AlignmentCursor, word: string): [number, number] | null {
  let first = -1
  let last = -1
  for (const char of word) {
    if (WHITESPACE_REGEX.test(char)) continue
    while (cursor.pos < cursor.text.length && WHITESPACE_REGEX.test(cursor.text[cursor.pos])) {
      cursor.pos++
    }
    if (cursor.text[cursor.pos] !== char) return null
    if (first < 0) first = cursor.pos
    last = cursor.pos
    cursor.pos++
  }
  if (first < 0) return null
  return [cursor.starts[first], cursor.ends[last]]
}

export function buildKaraokeTimeline(story: any): KaraokeSpan[] | null {
  const alignment = story?.audioAlignment
  if (
    !alignment?.text ||
    !Array.isArray(alignment.charStartsMs) ||
    !Array.isArray(alignment.charEndsMs) ||
    alignment.charStartsMs.length !== alignment.text.length ||
    alignment.charEndsMs.length !== alignment.text.length
  ) {
    return null
  }

  const cursor: AlignmentCursor = {
    text: alignment.text,
    starts: alignment.charStartsMs,
    ends: alignment.charEndsMs,
    pos: 0,
  }

  const spans: KaraokeSpan[] = []
  let sentence = 0
  let sentenceHasContent = false

  const addBlock = (wordObjects: any[], makeId: (index: number) => string): boolean => {
    if (sentenceHasContent) {
      sentence++
      sentenceHasContent = false
    }
    for (let index = 0; index < wordObjects.length; index++) {
      const raw = wordObjects[index]?.word ?? ''
      const stripped = raw.replace(TAG_REGEX, '')
      if (!stripped.length) continue // renders null — no DOM node, id still consumed
      // Whitespace-only word objects (stray spaces in the Prismic text) render
      // an invisible span but have no counterpart in the trimmed aligned text.
      if (!stripped.trim().length) continue
      const window = matchWord(cursor, stripped)
      if (!window) return false
      spans.push({
        id: makeId(index),
        startMs: window[0],
        endMs: window[1],
        sentence,
        isWord: WORD_CHAR_REGEX.test(stripped),
      })
      sentenceHasContent = true
      if (SENTENCE_END_REGEX.test(stripped)) {
        sentence++
        sentenceHasContent = false
      }
    }
    return true
  }

  const titleContent = story?.storyTitleContent || []
  if (!addBlock(titleContent, (index) => `STORY-TITLE${index}`)) return null

  const paragraphs = splitIntoParagraphs(story?.storyContent || [])
  for (let p = 0; p < paragraphs.length; p++) {
    if (!addBlock(paragraphs[p], (index) => `WORD-${p}${index}`)) return null
  }

  return spans.length ? spans : null
}
